import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { CalendarDays, Plus, CheckCircle2, Clock, Lock, ChevronLeft, User, ShieldAlert, Camera, Trash2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { t } from "../lib/i18n";
import PhotoAttach from "../components/PhotoAttach";

const PRIORITY_STYLE = {
    HIGH: "bg-red-50 text-[#E02B2B] border-red-200",
    NORMAL: "bg-gray-50 text-gray-700 border-gray-200",
};
const STATUS_STYLE = {
    OPEN: "bg-amber-50 text-amber-700 border-amber-200",
    DONE: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function renderPreset(p, lang, params) {
    let s = p?.[lang] || p?.en || "";
    Object.entries(params || {}).forEach(([k, v]) => { s = s.replaceAll(`{${k}}`, String(v || `{${k}}`)); });
    return s;
}

export default function DiaryPage() {
    const { user } = useAuth();
    const { siteId: urlSiteId } = useParams();
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState(urlSiteId || "");
    const [date, setDate] = useState(todayIso());
    const [data, setData] = useState(null);
    const [roster, setRoster] = useState([]);
    const [presets, setPresets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [staffView, setStaffView] = useState(null); // staff obj for phone-hand-over view
    const [showNew, setShowNew] = useState(false);

    useEffect(() => {
        api.get("/sites").then(({ data }) => { setSites(data); if (!siteId && data.length) setSiteId(data[0].id); });
        api.get("/job-presets").then(({ data }) => setPresets(data)).catch(() => {});
    }, []);

    const load = useCallback(async () => {
        if (!siteId) return;
        setLoading(true);
        try {
            const [d, r] = await Promise.all([
                api.get(`/sites/${siteId}/diary`, { params: { date } }),
                api.get(`/sites/${siteId}/roster`),
            ]);
            setData(d.data);
            setRoster(r.data || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId, date]);
    useEffect(() => { load(); }, [load]);

    const isLocked = data?.diary?.locked;
    const isHOorPM = user?.role === "HO_ADMIN" || user?.role === "PROJECT_MANAGER";

    async function closeDiary() {
        if (!window.confirm("Close the diary for this date? Undone jobs will carry over to tomorrow.")) return;
        try { const { data: r } = await api.post(`/sites/${siteId}/diary/close`, null, { params: { date } }); toast.success(`Diary closed — ${r.rolled_over} job(s) rolled over.`); load(); }
        catch (e) { toast.error(apiError(e)); }
    }

    async function verifyJob(j) {
        try { await api.post(`/diary/jobs/${j.id}/verify`); toast.success("Verified"); load(); }
        catch (e) { toast.error(apiError(e)); }
    }
    async function deleteJob(j) {
        if (!window.confirm(`Delete job "${j.description}"?`)) return;
        try { await api.delete(`/diary/jobs/${j.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    }

    if (loading || !data) return <div className="mx-auto max-w-4xl p-6"><div className="h-64 bg-white rounded-xl animate-pulse" /></div>;

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355] mb-3"><ChevronLeft className="w-4 h-4" /> {t(user, "nav_sites")}</Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{t(user, "diary_title")}</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><CalendarDays className="w-6 h-6" /> {sites.find((s) => s.id === siteId)?.name || "…"}</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {sites.length > 1 && <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="diary-site-select">
                        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>}
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="diary-date" />
                    {!isLocked && <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-3 py-2 rounded-md" data-testid="diary-new-job"><Plus className="w-4 h-4" /> {t(user, "diary_new_job")}</button>}
                    {!isLocked && !isHOorPM && <button onClick={closeDiary} className="inline-flex items-center gap-1 bg-white border border-red-200 text-[#E02B2B] text-sm font-semibold px-3 py-2 rounded-md hover:bg-red-50" data-testid="diary-close-btn"><Lock className="w-3.5 h-3.5" /> {t(user, "diary_close")}</button>}
                    {isLocked && <span className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-gray-100 text-gray-600 text-xs font-semibold uppercase tracking-wider" data-testid="diary-locked-badge"><Lock className="w-3.5 h-3.5" /> {t(user, "diary_locked")}</span>}
                </div>
            </div>

            {/* Context */}
            <div className="mt-6 rounded-xl bg-[#F4F6F9] border border-gray-200 p-4" data-testid="diary-context">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">{t(user, "diary_context")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <ContextBlock title={`${t(user, "diary_absences")} (${data.context.absences.length})`} data-testid="ctx-absences">
                        {data.context.absences.length ? data.context.absences.map((a, i) => (
                            <div key={i} className="text-gray-700"><strong>{a.staff_name}</strong> · {a.staff_role} · {a.reason} · {a.cover_arranged ? `Cover: ${a.cover_person_name}` : "No cover"}</div>
                        )) : <div className="text-gray-400">—</div>}
                    </ContextBlock>
                    <ContextBlock title={`${t(user, "diary_coaching_due")} (${data.context.coaching_due.length})`} data-testid="ctx-coaching">
                        {data.context.coaching_due.length ? data.context.coaching_due.map((c) => (
                            <div key={c.id} className="text-gray-700 flex items-center gap-1"><ShieldAlert className="w-3 h-3 text-amber-600" /> <strong>{c.role}</strong> · {c.course_title} · due {fmtDate(c.due_by)}</div>
                        )) : <div className="text-gray-400">—</div>}
                    </ContextBlock>
                    <ContextBlock title={`${t(user, "diary_compliance_today")} (${data.context.compliance_today.length})`} data-testid="ctx-compliance">
                        {data.context.compliance_today.length ? data.context.compliance_today.map((c) => (
                            <div key={c.id} className="text-gray-700"><strong>{c.name}</strong> · {c.vendor || "—"}</div>
                        )) : <div className="text-gray-400">—</div>}
                    </ContextBlock>
                </div>
            </div>

            {/* Jobs */}
            <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-[#1E3355]">{t(user, "diary_jobs")} ({data.jobs.length})</div>
                    <div className="flex items-center gap-2">
                        <select value={staffView?.id || ""} onChange={(e) => setStaffView(roster.find((r) => r.id === e.target.value) || null)} className="text-xs rounded border border-gray-300 bg-white px-2 py-1" data-testid="diary-staff-view">
                            <option value="">{t(user, "diary_all_view")}</option>
                            {roster.map((r) => <option key={r.id} value={r.id}>{t(user, "diary_view_as", { name: r.name })}</option>)}
                        </select>
                    </div>
                </div>
                <div className="space-y-2" data-testid="diary-jobs-list">
                    {(staffView ? data.jobs.filter((j) => j.assignee_staff_id === staffView.id) : data.jobs).length === 0 ? (
                        <div className="rounded-xl bg-white border border-gray-100 p-8 text-center text-sm text-gray-500" data-testid="diary-empty">No jobs {staffView ? `for ${staffView.name}` : "yet"}.</div>
                    ) : (
                        (staffView ? data.jobs.filter((j) => j.assignee_staff_id === staffView.id) : data.jobs).map((j) => (
                            <JobRow key={j.id} job={j} user={user} lang={user?.language || "en"} staffView={!!staffView} isLocked={isLocked} onVerify={verifyJob} onDelete={deleteJob} onChanged={load} siteId={siteId} />
                        ))
                    )}
                </div>
            </div>

            {showNew && (
                <NewJobModal siteId={siteId} date={date} roster={roster} presets={presets} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
            )}
        </div>
    );
}

function ContextBlock({ title, children, ...props }) {
    return (<div className="rounded-md bg-white border border-gray-100 p-3" {...props}>
        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{title}</div>
        <div className="space-y-1">{children}</div>
    </div>);
}

function JobRow({ job, user, lang, staffView, isLocked, onVerify, onDelete, onChanged, siteId }) {
    const [markingDone, setMarkingDone] = useState(false);
    const [note, setNote] = useState("");
    const [photoId, setPhotoId] = useState(null);
    // Render preset in staff language for staff view
    const description = job.preset_snapshot ? renderPreset(job.preset_snapshot, staffView ? lang : "en", job.params) : job.description;

    async function markDone() {
        if (job.photo_required && !photoId) { toast.error("A photo is required to mark this job done."); return; }
        try { await api.post(`/diary/jobs/${job.id}/done`, { photo_id: photoId, note }); toast.success("Marked done"); setMarkingDone(false); onChanged(); }
        catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid={`diary-job-${job.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLE[job.status]}`}>{job.status}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${PRIORITY_STYLE[job.priority]}`}>{job.priority}</span>
                        {job.verified && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle2 className="w-3 h-3" /> Verified</span>}
                        {job.carried_over_from_id && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200" data-testid={`carried-over-${job.id}`}>Carried over</span>}
                        {job.photo_required && !job.done_photo_id && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-[#E02B2B] bg-red-50 border border-red-200"><Camera className="w-3 h-3" /> Photo required</span>}
                    </div>
                    <div className="mt-1 text-sm font-semibold text-[#1E3355]">{description}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span><User className="w-3 h-3 inline" /> {job.assignee_staff_name} · {job.assignee_staff_role}</span>
                        {job.time_from && <span><Clock className="w-3 h-3 inline" /> {job.time_from}{job.time_to ? `–${job.time_to}` : ""}</span>}
                    </div>
                    {job.done_note && <div className="mt-1 text-[11px] text-gray-600 italic">Done: {job.done_note}</div>}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {!isLocked && staffView && job.status === "OPEN" && (
                        <button onClick={() => setMarkingDone(true)} className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md" data-testid={`job-mark-done-${job.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Mark done</button>
                    )}
                    {!isLocked && !staffView && job.status === "DONE" && !job.verified && (
                        <button onClick={() => onVerify(job)} className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-xs font-semibold px-3 py-1.5 rounded-md" data-testid={`job-verify-${job.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Verify</button>
                    )}
                    {!isLocked && !staffView && (
                        <button onClick={() => onDelete(job)} className="p-1.5 rounded hover:bg-red-50 text-[#E02B2B]" data-testid={`job-delete-${job.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                </div>
            </div>
            {markingDone && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    <textarea placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" data-testid={`job-done-note-${job.id}`} />
                    {job.photo_required && (
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B] mb-1">Photo required</div>
                            <PhotoAttach parentType="diary_job" parentId={job.id} siteId={siteId} tag="done" compact onChange={(photos) => setPhotoId(photos[0]?.id || null)} testIdPrefix={`job-photo-${job.id}`} />
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button onClick={() => setMarkingDone(false)} className="flex-1 py-2 rounded-md border border-gray-200 text-xs font-medium">Cancel</button>
                        <button onClick={markDone} className="flex-1 py-2 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold" data-testid={`job-done-submit-${job.id}`}>Save done</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function NewJobModal({ siteId, date, roster, presets, onClose, onCreated }) {
    const [mode, setMode] = useState("preset"); // preset | free
    const [presetKey, setPresetKey] = useState("");
    const [params, setParams] = useState({});
    const [description, setDescription] = useState("");
    const [assigneeId, setAssigneeId] = useState("");
    const [tf, setTf] = useState("");
    const [tt, setTt] = useState("");
    const [priority, setPriority] = useState("NORMAL");
    const [photoRequired, setPhotoRequired] = useState(false);
    const [saving, setSaving] = useState(false);

    const preset = presets.find((p) => p.key === presetKey);
    const requiredMissing = (preset?.placeholders || []).filter((ph) => !String(params[ph] || "").trim());
    const canSubmit = assigneeId && (mode === "free" ? description.trim().length > 0 : (presetKey && requiredMissing.length === 0));

    async function submit() {
        setSaving(true);
        try {
            const payload = {
                site_id: siteId, date, assignee_staff_id: assigneeId,
                priority, photo_required: photoRequired,
                time_from: tf || null, time_to: tt || null,
            };
            if (mode === "preset") { payload.preset_key = presetKey; payload.params = params; }
            else { payload.description = description; }
            await api.post(`/sites/${siteId}/diary/jobs`, payload);
            toast.success("Job added");
            onCreated();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="diary-new-modal">
            <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <div><div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">Diary</div>
                    <div className="font-display font-semibold text-[#1E3355] mt-0.5">New job</div></div>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <div className="p-5 space-y-3 overflow-y-auto">
                    <div className="flex gap-2">
                        <button onClick={() => setMode("preset")} className={`flex-1 py-1.5 rounded-md text-xs font-semibold border ${mode === "preset" ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-600 border-gray-200"}`} data-testid="job-mode-preset">Preset</button>
                        <button onClick={() => setMode("free")} className={`flex-1 py-1.5 rounded-md text-xs font-semibold border ${mode === "free" ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-600 border-gray-200"}`} data-testid="job-mode-free">Free text</button>
                    </div>
                    {mode === "preset" ? (
                        <>
                            <Field label="Preset">
                                <select value={presetKey} onChange={(e) => { setPresetKey(e.target.value); setParams({}); }} className={fieldCls} data-testid="job-preset-select">
                                    <option value="">Pick a preset…</option>
                                    {presets.map((p) => <option key={p.key} value={p.key}>{`${p.key} — ${p.en}`}</option>)}
                                </select>
                            </Field>
                            {preset && preset.placeholders?.map((ph) => (
                                <Field key={ph} label={`Fill in {${ph}}`}>
                                    <input value={params[ph] || ""} onChange={(e) => setParams({ ...params, [ph]: e.target.value })} className={fieldCls} data-testid={`job-param-${ph}`} />
                                </Field>
                            ))}
                            {preset && <div className="text-[11px] text-gray-500 italic">Preview: {renderPreset(preset, "en", params)}</div>}
                        </>
                    ) : (
                        <Field label="Description">
                            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className={fieldCls} data-testid="job-desc" />
                        </Field>
                    )}
                    <Field label="Assign to">
                        <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={fieldCls} data-testid="job-assignee">
                            <option value="">Pick roster staff…</option>
                            {roster.map((r) => <option key={r.id} value={r.id}>{r.name} · {r.role}</option>)}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Time from"><input type="time" value={tf} onChange={(e) => setTf(e.target.value)} className={fieldCls} data-testid="job-time-from" /></Field>
                        <Field label="Time to"><input type="time" value={tt} onChange={(e) => setTt(e.target.value)} className={fieldCls} data-testid="job-time-to" /></Field>
                    </div>
                    <Field label="Priority">
                        <select value={priority} onChange={(e) => setPriority(e.target.value)} className={fieldCls} data-testid="job-priority">
                            <option value="NORMAL">Normal</option><option value="HIGH">High</option>
                        </select>
                    </Field>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={photoRequired} onChange={(e) => setPhotoRequired(e.target.checked)} data-testid="job-photo-required" /> Require photo proof
                    </label>
                </div>
                <div className="flex border-t border-gray-100">
                    <button onClick={onClose} className="flex-1 py-3 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                    <button onClick={submit} disabled={!canSubmit || saving} className="flex-1 py-3 text-sm font-semibold text-white bg-[#1E3355] hover:bg-[#15243C] disabled:opacity-50" data-testid="job-submit">{saving ? "Adding…" : "Add job"}</button>
                </div>
            </div>
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
function Field({ label, children }) { return (<div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>); }
